import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Languages } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { t } from "../lib/i18n";
import Logo from "./Logo";
import { Button } from "./ui/button";
import { FollowupBell } from "./followups";
import { NotificationBell } from "./NotificationBell";
import { ResearchReminder } from "./research/ResearchReminder";

const navItems = [
  { to: "/",          key: "home" },
  { to: "/services",  key: "services" },
  { to: "/store",     key: "store" },
  { to: "/submit",    key: "submit" },
  { to: "/track",     key: "track" },
  { to: "/branches",  key: "branches" },
  { to: "/partner/login", key: "partner" },
  { to: "/admin/login",   key: "staff" },
];

export const Header = () => {
  const { lang, setLang, tr } = useLang();
  const [open, setOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive
        ? "text-teal-700 bg-teal-50"
        : "text-slate-700 hover:text-teal-700 hover:bg-slate-100"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `block w-full px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
      isActive
        ? "text-teal-700 bg-teal-50"
        : "text-slate-700 hover:text-teal-700 hover:bg-slate-100"
    }`;

  return (
    <header
      className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-brand-link" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="primary">
          {navItems.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.to === "/"}
              className={linkClass}
              data-testid={`nav-${item.key}`}
            >
              {tr(t.nav[item.key])}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <FollowupBell />
          <NotificationBell />
          <ResearchReminder />
          <button
            type="button"
            onClick={() => setLang(lang === "en" ? "hi" : "en")}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors"
            aria-label={`Switch language, current ${lang}`}
            data-testid="lang-toggle"
          >
            <Languages className="w-4 h-4" aria-hidden />
            <span className="uppercase tracking-wide">{lang === "en" ? "हिं" : "EN"}</span>
          </button>

          <Link to="/submit" className="hidden sm:block">
            <Button
              size="sm"
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-md focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              data-testid="header-apply-btn"
            >
              {tr(t.hero.ctaPrimary)}
            </Button>
          </Link>

          <button
            type="button"
            className="lg:hidden p-2 rounded-md text-slate-700 hover:bg-slate-100"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                className={mobileLinkClass}
                data-testid={`mobile-nav-${item.key}`}
              >
                {tr(t.nav[item.key])}
              </NavLink>
            ))}
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setLang(lang === "en" ? "hi" : "en")}
                className="flex-1 px-3 py-2 rounded-md text-sm font-medium border border-slate-200 text-slate-700"
                data-testid="mobile-lang-toggle"
              >
                <Languages className="inline w-4 h-4 mr-1" /> {lang === "en" ? "हिन्दी" : "English"}
              </button>
              <Link to="/submit" onClick={() => setOpen(false)} className="flex-1">
                <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="mobile-apply-btn">
                  {tr(t.hero.ctaPrimary)}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
